import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { FileImage } from '../../components/ui/ApiImage';
import { CopyableCode } from '../../components/ui/CopyableCode';
import { Fold } from '../../components/ui/Fold';
import { Hint } from '../../components/ui/Hint';
import { Markdown } from '../../components/ui/Markdown';
import { ImageCropDialog } from '../../components/ui/ImageCropDialog';
import { useAuth } from '../auth/AuthContext';
import { PrintDialog } from '../labels/PrintDialog';
import { listStocktakes, openStocktake } from '../stock/api';
import { MoveDialog } from './MoveDialog';
import { PhotoViewer } from './PhotoViewer';
import {
  archiveLocation,
  attachImage,
  detachImage,
  getLocation,
  listChildren,
  setPrimaryImage,
  updateLocation,
  uploadFile,
  LOCATION_KINDS,
  type LocationKind,
} from './api';

interface Props {
  locationId: string;
  onSelect: (id: string) => void;
  onArchived: () => void;
}

export function LocationPanel({ locationId, onSelect, onArchived }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [cropping, setCropping] = useState<File | null>(null);

  const canEdit = can('catalog.write');
  const canStocktake = can('stocktake.run');

  const { data: location, isLoading } = useQuery({
    queryKey: ['locations', 'detail', locationId],
    queryFn: () => getLocation(locationId),
  });

  const { data: children = [] } = useQuery({
    queryKey: ['locations', 'children', locationId],
    queryFn: () => listChildren(locationId),
  });

  const { data: stocktakes = [], isLoading: stocktakesLoading } = useQuery({
    queryKey: ['stocktakes'],
    queryFn: listStocktakes,
    enabled: canStocktake,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['locations'] });
  }

  function fail(exc: unknown) {
    setError(exc instanceof ApiError ? exc.code : 'unknown');
  }

  const save = useMutation({
    mutationFn: (input: { code: string; name: string; kind: LocationKind; description: string }) =>
      updateLocation(locationId, input),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const archive = useMutation({
    mutationFn: () => archiveLocation(locationId),
    onSuccess: () => {
      refresh();
      onArchived();
    },
    onError: fail,
  });

  const addPhotos = useMutation({
    mutationFn: async (files: File[]) => {
      // Sequential rather than parallel: uploads are deduplicated by content
      // hash, and two identical files racing would both miss the existing row
      // and collide on the unique digest.
      for (const file of files) {
        const stored = await uploadFile(file);
        await attachImage(locationId, stored.id);
      }
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const makePrimary = useMutation({
    mutationFn: (imageId: string) => setPrimaryImage(locationId, imageId),
    onSuccess: refresh,
    onError: fail,
  });

  const dropPhoto = useMutation({
    mutationFn: (imageId: string) => detachImage(locationId, imageId),
    onSuccess: () => {
      setViewing(null);
      refresh();
    },
    onError: fail,
  });

  const startStocktake = useMutation({
    mutationFn: () => openStocktake({ location_id: locationId, include_descendants: false }),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['stocktakes'] });
      navigate(`/stocktakes/${session.id}`);
    },
    onError: fail,
  });

  function acceptFiles(list: FileList | null) {
    const files = Array.from(list ?? []).filter((file) => file.type.startsWith('image/'));
    if (files.length) addPhotos.mutate(files);
  }

  if (isLoading || !location) return <p className="panel__hint">{t('common.loading')}</p>;

  const openImage = location.images.find((image) => image.id === viewing);
  const ancestorIds = new Set(location.breadcrumbs.map((crumb) => crumb.id));
  const currentStocktake = stocktakes.find((session) => session.status === 'open' && (
    session.location_id === null ||
    session.location_id === location.id ||
    (session.include_descendants && session.location_id !== null && ancestorIds.has(session.location_id))
  ));

  return (
    <section className="panel">
      <nav className="panel__crumbs">
        {location.breadcrumbs.map((crumb) => (
          <button key={crumb.id} type="button" onClick={() => onSelect(crumb.id)}>
            {crumb.name}
          </button>
        ))}
        <span>{location.name}</span>
      </nav>

      {error && (
        <p className="panel__error">
          {t(`locations.errors.${error}`, t('locations.errors.unknown'))}
        </p>
      )}

      {editing ? (
        <form
          className="panel__form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            save.mutate({
              code: String(form.get('code')),
              name: String(form.get('name')),
              kind: String(form.get('kind')) as LocationKind,
              description: String(form.get('description') ?? ''),
            });
          }}
        >
          <label>
            {t('locations.code')}
            <input name="code" defaultValue={location.code} required />
          </label>
          <label>
            {t('locations.name')}
            <input name="name" defaultValue={location.name} required />
          </label>
          <label>
            {t('locations.kindLabel')}
            <select name="kind" defaultValue={location.kind} required>
              {LOCATION_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`locations.kind.${kind}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('locations.description')}
            <textarea name="description" defaultValue={location.description ?? ''} rows={6} />
            <span className="component-form__parsed">{t('common.markdownHint')}</span>
          </label>
          <div className="panel__actions">
            <button type="submit" disabled={save.isPending}>
              {t('common.save')}
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <header className="panel__head">
          <div>
            <h2>{location.name}</h2>
            <p className="panel__meta">
              <CopyableCode
                value={location.code}
                className="components__code"
                label={t('locations.code')}
              />{' '}
              · {t(`locations.kind.${location.kind}`)} ·{' '}
              {t('locations.childrenCount', { n: location.children_count })}
            </p>
          </div>
          <div className="panel__actions">
            {can('label.print') && (
              <button type="button" onClick={() => setPrinting(true)}>
                {t('labels.print')}
              </button>
            )}
            {canEdit && (
              <>
                <button type="button" onClick={() => setEditing(true)}>
                  {t('locations.edit')}
                </button>
                <div className="panel__move">
                  <button type="button" onClick={() => setMoving(true)}>
                    {t('locations.move')}
                  </button>
                  {moving && (
                    <MoveDialog
                      location={location}
                      onDone={() => {
                        setMoving(false);
                        refresh();
                      }}
                      onCancel={() => setMoving(false)}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="danger"
                  onClick={() => archive.mutate()}
                  disabled={archive.isPending}
                >
                  {t('locations.archive')}
                </button>
              </>
            )}
          </div>
        </header>
      )}

      {printing && (
        <PrintDialog
          entityKind="location"
          entityId={location.id}
          onClose={() => setPrinting(false)}
        />
      )}

      {/* What the place is, and what it looks like, side by side. The photograph
          is how a shelf is recognised from the doorway and the description is
          what it is for; neither is worth a screenful of its own, and stacked
          they pushed the contents — the reason the card was opened — below the
          fold. */}
      <div className="panel__overview">
        <div className="panel__overview-main">
          {/* Formatted like a component's: a place's description is where the
              "third shelf from the top, behind the connectors" goes, and that is
              a list as often as it is a sentence. */}
          {location.description && <Markdown source={location.description} lines={4} />}
          <section className="location-stocktake">
            <div>
              <strong>{t('locations.balanceReportTitle')}</strong>
              <p>{t('locations.balanceReportHint')}</p>
            </div>
            <button type="button" onClick={() => navigate(`/stock?tab=locations&location=${location.id}`)}>
              {t('locations.openBalanceReport')}
            </button>
          </section>
          {canStocktake && (
            <section className="location-stocktake">
              <div>
                <strong>{t('locations.stocktakeTitle')}</strong>
                <p>{t(currentStocktake ? 'locations.stocktakeCurrentHint' : 'locations.stocktakeNewHint')}</p>
              </div>
              {stocktakesLoading ? (
                <button type="button" disabled>{t('common.loading')}</button>
              ) : currentStocktake ? (
                <button type="button" className="primary" onClick={() => navigate(`/stocktakes/${currentStocktake.id}`)}>
                  {t('locations.openStocktake', { code: currentStocktake.code })}
                </button>
              ) : (
                <button type="button" className="primary" disabled={startStocktake.isPending} onClick={() => startStocktake.mutate()}>
                  {t('locations.startStocktake')}
                </button>
              )}
            </section>
          )}
        </div>

        <section
          className={`gallery${dropActive ? ' gallery--drop' : ''}`}
          onDragOver={(event) => {
            if (!canEdit) return;
            // Both handlers must preventDefault or the browser navigates to the
            // dropped file instead of handing it over.
            event.preventDefault();
            setDropActive(true);
          }}
          onDragLeave={() => setDropActive(false)}
          onDrop={(event) => {
            if (!canEdit) return;
            event.preventDefault();
            setDropActive(false);
            acceptFiles(event.dataTransfer.files);
          }}
        >
          <header className="gallery__head">
            <h3>{t('locations.photos')}</h3>
            {canEdit && (
              <>
                <Hint text={t('locations.dropHint')} />
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  hidden
                  onChange={(event) => {
                    acceptFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="gallery__add"
                  onClick={() => fileInput.current?.click()}
                  disabled={addPhotos.isPending}
                  title={t('locations.addPhoto')}
                  aria-label={t('locations.addPhoto')}
                >
                  {addPhotos.isPending ? '…' : '+'}
                </button>
                <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) setCropping(file); event.target.value = ''; }} />
                <button type="button" className="gallery__add" onClick={() => cameraInput.current?.click()} disabled={addPhotos.isPending} title={t('images.takePhoto')} aria-label={t('images.takePhoto')}>{String.fromCharCode(8961)}</button>
              </>
            )}
          </header>

          {location.images.length === 0 ? (
            <p className="panel__hint">{t('locations.noPhotos')}</p>
          ) : (
            <ul className="gallery__grid">
              {location.images.map((image) => (
                <li key={image.id}>
                  <button
                    type="button"
                    className="gallery__item"
                    onClick={() => setViewing(image.id)}
                    title={image.caption ?? ''}
                  >
                    <FileImage
                      fileId={image.file_id}
                      variant="thumb"
                      alt={image.caption ?? t('locations.photo')}
                      loading="lazy"
                    />
                    {image.is_primary && <span className="gallery__badge">★</span>}
                  </button>
                  {canEdit && (
                    <div className="gallery__actions">
                      {!image.is_primary && (
                        <button type="button" onClick={() => makePrimary.mutate(image.id)}>
                          {t('locations.makePrimary')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        onClick={() => dropPhoto.mutate(image.id)}
                      >
                        {t('locations.removePhoto')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {cropping && <ImageCropDialog file={cropping} onCancel={() => setCropping(null)} onReady={(file) => { setCropping(null); addPhotos.mutate([file]); }} />}

      {openImage && (
        <PhotoViewer
          image={openImage}
          childLocations={children}
          canEdit={canEdit}
          onOpenChild={(id) => {
            setViewing(null);
            onSelect(id);
          }}
          onClose={() => setViewing(null)}
        />
      )}

      {/* Open, unlike the folds on a component's card, and the difference is
          deliberate: what a place contains is not a detail of the place, it is
          the place. Shut, this card would be a name and a photograph. The count
          rides on the heading all the same, so a cabinet of forty shelves says
          so before the eye reaches the end of the list. */}
      {children.length > 0 && (
        <Fold title={t('locations.contents')} count={children.length} open className="panel__children">
          <ul>
            {children.map((child) => (
              <li key={child.id}>
                <button type="button" onClick={() => onSelect(child.id)}>
                  <span>{child.name}</span>
                  <code>{child.code}</code>
                </button>
              </li>
            ))}
          </ul>
        </Fold>
      )}
    </section>
  );
}
